import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { NgProgress } from '@ngx-progressbar/core';
import { ToastrService } from 'ngx-toastr';
import { APIOperableComponent } from '../../../../shared/components/api-operable/api-operable.component';
import {
  KEYSTROKE,
  PREVENT_KEYUP_EVENTS,
  SEARCH_QUITE_PERIOD
} from '../../../../shared/utils/miscellaneous.utils';
import { SortUtils } from '../../../../shared/utils/sort.utils';
import { AuthService } from '../../../security/service/auth.service';
import { UsersService } from '../../../user/service/users.service';
import { CustomerUserListColumnsModel } from '../../model/customer-user-list-columns.model';
import { CustomerUserModel } from '../../model/customer-user.model';

@Component({
  selector: 'customer-user',
  templateUrl:
    './console/app-js/modules/customerUser/components/customer-user/customer-user.component.html'
})
export class CustomerUserComponent extends APIOperableComponent
  implements OnInit {
  @ViewChild('searchInput', { static: true }) searchInput: ElementRef;
  columns = CustomerUserListColumnsModel;
  protected allCustomersData: Array<{ id: string; text: string }> = [];
  public selectedCustomerType: any;
  public selectedCustomerNumber: string = '';
  protected allCustomers: Array<{ id: string; text: string }> = [];
  private infiniteScroll: boolean = false;
  private customerUserData: any;
  public showResetPasswordModal: boolean = false;
  public user: CustomerUserModel;
  data: Array<CustomerUserModel> = [];
  public noRecordsSettings = {
    message: '',
    styleClass: 'no-records-wrapper'
  };
  public filterValue = '';
  private typingTimeout: any;
  private hasNextPage: boolean = false;
  private filterSearch: boolean = true;
  private originalUserData: Array<CustomerUserModel> = [];
  public userModalTitle: string;
  public openUserModal = false;
  public static CUSTOMERADD = 'Add User';
  public static CUSTOMEREDIT = 'Edit User';
  public static RESETPASSWORDCOLUMNINDEX = 4;
  public customerUser: CustomerUserModel;

  ngOnInit() {
    this.loadCustomers();
  }

  constructor(
    progressService: NgProgress,
    toastr: ToastrService,
    authService: AuthService,
    private usersService: UsersService
  ) {
    super(progressService, toastr, authService);
  }
  onReset(): void {
    this.data = this.originalUserData;
    this.setDefaultNoRecordSettings();
    this.hasNextPage = false;
  }

  /**
   * Loads a list of customers
   * @returns {Observable<any>}
   */
  loadCustomers() {
    this.usersService.getCustomers().subscribe((res: any) => {
      if (res.results) {
        let customers = res.results.map(customer => {
          return {
            id: customer.number,
            text: `${customer.name} (${customer.number})`
          };
        });
        this.allCustomers = customers.sort((a, b) =>
          SortUtils.compareByProperty(a, b, 'text')
        );
        this.allCustomersData = this.allCustomers;
      }
    });
  }

  loadUserData() {
    this.loadingIndicator = true;
    this.usersService
      .getUsersByCustomerUserManger(
        this.filterValue,
        this.selectedCustomerNumber
      )
      .subscribe(
        result => {
          this.customerUserData = result.users;
          if (this.filterValue == '') {
            this.originalUserData = this.customerUserData;
          }
          let dataCheck = JSON.parse(JSON.stringify(this.data));
          if (dataCheck !== null && this.infiniteScroll) {
            dataCheck.push(...this.customerUserData);
            this.data = [...dataCheck];
            this.infiniteScroll = false;
          } else if (this.filterSearch === true) {
            this.data = [...this.customerUserData];
            this.filterSearch = false;
          }
          if (this.customerUserData.length === 0) {
            this.data = [];
            this.setDefaultNoRecordSettings();
            this.noRecordsSettings.message = 'No Results Found';
            this.noRecordsSettings.styleClass = null;
          }
          this.loadingIndicator = false;
          this.hasNextPage = result.hasNextPage;
          setTimeout(() => {
            this.searchInput.nativeElement.focus();
          }, 100);
        },
        error => {
          console.error(error);
          this.loadingIndicator = false;
          this.data = [];
        }
      );
  }

  /**
   * Infinity scrolling pagination functionality
   */
  infinityScroll(): void {
    if (this.hasNextPage) {
      this.infiniteScroll = true;
      this.loadUserData();
    }
  }

  onSearchCustomerNumber(): void {
    if (!this.selectedCustomerNumber) {
      this.data = [];
      this.setDefaultNoRecordSettings();
      this.noRecordsSettings.message = 'No Results Found';
      this.noRecordsSettings.styleClass = null;
    } else {
      this.loadUserData();
    }
  }
  /**
   * Set No Records Settings to Default state.
   */
  private setDefaultNoRecordSettings(): void {
    this.noRecordsSettings = {
      message: '',
      styleClass: 'no-records-wrapper'
    };
  }
  /**
   * Sets customerId with required selection.
   */
  public valueChangeCustomer(filter: any): void {
    this.filterValue = '';
    this.data = [];
    if (filter) {
      this.selectedCustomerNumber = filter.id;
      this.onSearchCustomerNumber();
      this.infiniteScroll = true;
    } else {
      this.selectedCustomerNumber = null;
      this.setDefaultNoRecordSettings();
    }
  }
  protected valueChange(value) {
    let temp = this.allCustomersData.filter(row => {
      return row.text
        ? row.text.toLowerCase().indexOf(value.toLowerCase()) !== -1 || !value
        : '';
    });

    this.allCustomers = value !== '' ? temp : this.allCustomersData;
  }

  onKeyUp($event: any): void {
    if (!this.filterValue || this.filterValue.length < 3) {
      this.onReset();
      return;
    }
    this.filterSearch = true;
    if ($event.code === KEYSTROKE.ENTER) {
      this.loadUserData();
    } else if (!PREVENT_KEYUP_EVENTS.test($event.code)) {
      clearTimeout(this.typingTimeout);
      this.typingTimeout = setTimeout(
        () => this.loadUserData(),
        SEARCH_QUITE_PERIOD
      );
    }
  }

  /**
   * Open the Edit User groups modal.
   * @param user
   */
  onAddUser(event: any): void {
    if (
      event &&
      event.columnIndex === CustomerUserComponent.RESETPASSWORDCOLUMNINDEX
    ) {
      this.showResetPasswordModal = true;
      this.user = event.dataItem;
    } else {
      this.userModalTitle = CustomerUserComponent.CUSTOMERADD;
      this.customerUser = null;
      if (event) {
        this.userModalTitle = CustomerUserComponent.CUSTOMEREDIT;
        const user = this.data.find(x => event.dataItem.id === x.id);
        this.onEditUser(user);
      }
      if (this.selectedCustomerNumber) {
        this.openUserModal = true;
      } else {
        this.openUserModal = false;
      }
    }
  }

  /**
   * Close and cancel password reset modal
   */
  public close($event: any) {
    this.openUserModal = false;
    this.showResetPasswordModal = false;
    if ($event && $event.isActionPerformed) {
      this.filterSearch = true;
      this.data = [];
      this.loadUserData();
    }
  }

  /**
   * On Row cell click handler.
   * @param $event
   */
  onCellClick($event): void {
    if (
      $event &&
      $event.columnIndex === CustomerUserComponent.RESETPASSWORDCOLUMNINDEX
    ) {
      this.user = $event.dataItem;
      this.showResetPasswordModal = true;
    } else {
      const user = this.data.find(x => $event.dataItem.id === x.id);
      this.onEditUser(user);
    }
  }

  /**
   * Open the Edit User modal.
   * @param user
   */
  onEditUser(custUser: CustomerUserModel): void {
    this.userModalTitle = CustomerUserComponent.CUSTOMEREDIT;
    this.customerUser = custUser;
    this.customerUser.isEdit = true;
    this.openUserModal = true;
  }
}
